import { useMemo } from 'react';
import { tiltToPitchRatio, azimuthToOrientation } from '@/utils/roofEdgeCalculations';

export interface RoofVertex {
  lat: number;
  lng: number;
}

export interface RoofEdge {
  from: RoofVertex;
  to: RoofVertex;
  type: 'RIDGE' | 'HIP' | 'VALLEY' | 'RAKE' | 'EAVE';
}

export interface RoofPlane {
  id: string;
  index: number;
  areaSqFt: number;
  pitchDegrees: number;
  pitchRatio: string;
  azimuthDegrees: number;
  orientationLabel: string;
  vertices: RoofVertex[];
  center: RoofVertex;
}

export interface RoofGeometry {
  planes: RoofPlane[];
  edges: RoofEdge[];
  center: RoofVertex;
}

interface InputPlane {
  id: string;
  areaSqFt: number;
  tiltDegrees: number | null;
  azimuthDegrees: number | null;
  vertices: RoofVertex[];
  center?: RoofVertex;
}

export function useRoofGeometry(planes: InputPlane[]): RoofGeometry {
  return useMemo(() => {
    if (!planes || planes.length === 0) {
      return { planes: [], edges: [], center: { lat: 0, lng: 0 } };
    }

    // Calculate overall center from all vertices
    const allVertices = planes.flatMap(p => p.vertices || []).filter(v => v && v.lat !== undefined && v.lng !== undefined);
    const center = allVertices.length > 0
      ? {
          lat: allVertices.reduce((sum, v) => sum + v.lat, 0) / allVertices.length,
          lng: allVertices.reduce((sum, v) => sum + v.lng, 0) / allVertices.length,
        }
      : { lat: 0, lng: 0 };

    // Transform planes
    const transformedPlanes: RoofPlane[] = planes.map((plane, index) => {
      const pitchDegrees = plane.tiltDegrees ?? 0;
      const azimuthDegrees = plane.azimuthDegrees ?? 0;
      const pitchInfo = tiltToPitchRatio(pitchDegrees);
      const orientation = azimuthToOrientation(azimuthDegrees);

      // Calculate plane center if not provided
      const planeCenter = plane.center || (plane.vertices.length > 0 ? {
        lat: plane.vertices.reduce((sum, v) => sum + v.lat, 0) / plane.vertices.length,
        lng: plane.vertices.reduce((sum, v) => sum + v.lng, 0) / plane.vertices.length,
      } : center);

      return {
        id: plane.id,
        index: index + 1,
        areaSqFt: plane.areaSqFt,
        pitchDegrees,
        pitchRatio: pitchInfo?.pitch || '0/12',
        azimuthDegrees,
        orientationLabel: orientation || 'N/A',
        vertices: plane.vertices || [],
        center: planeCenter,
      };
    });

    // Infer edges from plane adjacency
    const edges: RoofEdge[] = inferEdges(transformedPlanes);

    return {
      planes: transformedPlanes,
      edges,
      center,
    };
  }, [planes]);
}

function inferEdges(planes: RoofPlane[]): RoofEdge[] {
  const edges: RoofEdge[] = [];
  const edgeMap = new Map<string, { from: RoofVertex; to: RoofVertex; planes: number[] }>();

  // Helper to create edge key (sorted to ensure consistency)
  const makeEdgeKey = (v1: RoofVertex, v2: RoofVertex) => {
    const key1 = `${v1.lat.toFixed(8)},${v1.lng.toFixed(8)}`;
    const key2 = `${v2.lat.toFixed(8)},${v2.lng.toFixed(8)}`;
    return key1 < key2 ? `${key1}-${key2}` : `${key2}-${key1}`;
  };

  // Collect all edges from all planes
  planes.forEach((plane) => {
    const vertices = plane.vertices;
    for (let i = 0; i < vertices.length; i++) {
      const from = vertices[i];
      const to = vertices[(i + 1) % vertices.length];
      const key = makeEdgeKey(from, to);

      if (!edgeMap.has(key)) {
        edgeMap.set(key, { from, to, planes: [] });
      }
      edgeMap.get(key)!.planes.push(plane.index);
    }
  });

  // Classify edges based on adjacency and pitch
  edgeMap.forEach(({ from, to, planes: planesIndices }) => {
    let edgeType: RoofEdge['type'] = 'RAKE';

    if (planesIndices.length === 2) {
      // Shared edge between two planes
      const plane1 = planes.find(p => p.index === planesIndices[0]);
      const plane2 = planes.find(p => p.index === planesIndices[1]);

      if (plane1 && plane2) {
        const pitchDiff = Math.abs(plane1.pitchDegrees - plane2.pitchDegrees);
        const azimuthDiff = Math.abs(plane1.azimuthDegrees - plane2.azimuthDegrees);

        // Ridge: opposite facing planes with similar pitch
        if (azimuthDiff > 135 && azimuthDiff < 225 && pitchDiff < 10) {
          edgeType = 'RIDGE';
        }
        // Hip: converging planes
        else if (azimuthDiff < 135 && pitchDiff < 15) {
          edgeType = 'HIP';
        }
        // Valley: converging planes with larger pitch difference
        else if (azimuthDiff < 135 && pitchDiff >= 15) {
          edgeType = 'VALLEY';
        }
      }
    } else if (planesIndices.length === 1) {
      // Perimeter edge - rake or eave
      const plane = planes.find(p => p.index === planesIndices[0]);
      if (plane) {
        // Simple heuristic: if edge is more perpendicular to slope direction, it's an eave
        const edgeAngle = Math.atan2(to.lat - from.lat, to.lng - from.lng) * (180 / Math.PI);
        const angleDiff = Math.abs(edgeAngle - plane.azimuthDegrees);
        edgeType = angleDiff < 45 || angleDiff > 315 ? 'RAKE' : 'EAVE';
      }
    }

    edges.push({ from, to, type: edgeType });
  });

  return edges;
}
